import React, { useEffect, useRef, useState } from "react";
import {
  Box,
  HStack,
  Image,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from "@chakra-ui/react";
import applePassLockScreen from "assets/apple-pass-lock-screen.png";
import applePassWalletBack from "assets/apple-pass-wallet-back.png";
import googleWalletPassFront from "assets/google-wallet-pass-front.png";
import googleWalletPassBack from "assets/google-wallet-pass-back.png";

// Fixed dimensions for images
const LOCK_SCREEN_WIDTH = 300;
const LOCK_SCREEN_HEIGHT = 600;
const WALLET_BACK_WIDTH = 300;
const WALLET_BACK_HEIGHT = 600;
const PREVIEW_SCALE = 0.8;
const PREVIEW_HEIGHT_OFFSET = `-${LOCK_SCREEN_HEIGHT * (1 - PREVIEW_SCALE)}px`;

export type NotificationPreviewType = "push" | "geo";

interface ImagePreviewProps {
  businessDisplayName: string;
  lockScreenMessage: string;
  // null = hide the back-of-pass "Latest Update" heading and body entirely.
  backOfPassMessage: string | null;
  isGoogleTabDisabled?: boolean;
  hidePassBack?: boolean;
}

export const ImagePreview = ({
  businessDisplayName,
  lockScreenMessage,
  backOfPassMessage,
  isGoogleTabDisabled = false,
  hidePassBack = false,
}: ImagePreviewProps) => {
  const showBackOfPassContent = backOfPassMessage !== null && !hidePassBack;
  const [imagesVisible, setImagesVisible] = useState(true);
  const [tabIndex, setTabIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isGoogleTabDisabled && tabIndex === 1) {
      setTabIndex(0);
    }
  }, [isGoogleTabDisabled, tabIndex]);

  // Check if images can fit in the container
  useEffect(() => {
    const checkSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.clientWidth;
        const requiredWidth = hidePassBack
          ? LOCK_SCREEN_WIDTH * PREVIEW_SCALE
          : (LOCK_SCREEN_WIDTH + WALLET_BACK_WIDTH + 20) * PREVIEW_SCALE;
        setImagesVisible(containerWidth >= requiredWidth);
      }
    };

    checkSize();
    window.addEventListener("resize", checkSize);
    return () => window.removeEventListener("resize", checkSize);
  }, [hidePassBack]);

  return (
    <VStack w="100%" spacing={4} ref={containerRef}>
      <Tabs index={tabIndex} onChange={setTabIndex} w="100%">
        <TabList justifyContent="center" border={0} color={"cherryBorder.800"}>
          <Tab
            pl={24}
            pr={24}
            borderColor={"cherryBorder.100"}
            _selected={{ color: "cherryRed.100", borderColor: "cherryRed.100" }}
          >
            Apple
          </Tab>
          <Tab
            pl={isGoogleTabDisabled ? 6 : 24}
            pr={isGoogleTabDisabled ? 6 : 24}
            borderColor={"cherryBorder.100"}
            isDisabled={isGoogleTabDisabled}
            _selected={{ color: "cherryRed.100", borderColor: "cherryRed.100" }}
            _disabled={{
              color: "gray.400",
              cursor: "not-allowed",
              opacity: 1,
            }}
          >
            <HStack spacing={2}>
              <Text>Google</Text>
              {isGoogleTabDisabled && (
                <Box
                  border="1px solid"
                  borderColor="gray.300"
                  color="gray.500"
                  borderRadius="full"
                  px={2}
                  py={0.5}
                  fontSize="xs"
                  fontWeight="normal"
                >
                  Not supported
                </Box>
              )}
            </HStack>
          </Tab>
        </TabList>
        <TabPanels>
          {/* Apple Tab */}
          <TabPanel>
            <HStack
              justifyContent="center"
              w="100%"
              overflow="hidden"
              transform={imagesVisible ? `scale(${PREVIEW_SCALE})` : undefined}
              transformOrigin="top center"
              mb={imagesVisible ? PREVIEW_HEIGHT_OFFSET : undefined}
            >
              {imagesVisible ? (
                <>
                  <Box position="relative" w={`${LOCK_SCREEN_WIDTH}px`}>
                    <Image
                      src={applePassLockScreen}
                      alt="Apple Pass Lock Screen"
                      width={LOCK_SCREEN_WIDTH}
                      height={LOCK_SCREEN_HEIGHT}
                      objectFit="contain"
                    />
                    <Box
                      position="absolute"
                      top="409px"
                      left="74px"
                      width="160px"
                      textAlign="left"
                    >
                      <Text
                        fontWeight="bold"
                        fontSize="sm"
                        lineHeight="shorter"
                        mb={1}
                        width={"150px"}
                        isTruncated
                      >
                        {businessDisplayName}
                      </Text>
                      <Text
                        fontSize="xs"
                        lineHeight="shorter"
                        noOfLines={4}
                        overflow="hidden"
                      >
                        {lockScreenMessage}
                      </Text>
                    </Box>
                  </Box>
                  {!hidePassBack && (
                    <Box position="relative" w={`${WALLET_BACK_WIDTH}px`}>
                      <Image
                        src={applePassWalletBack}
                        alt="Apple Pass Wallet Back"
                        width={WALLET_BACK_WIDTH}
                        height={WALLET_BACK_HEIGHT}
                        objectFit="contain"
                      />
                      {showBackOfPassContent && (
                        <>
                          <Box
                            position="absolute"
                            bottom="37%"
                            left="48%"
                            transform="translateX(-50%)"
                            width="80%"
                            maxWidth="220px"
                            textAlign="left"
                            borderRadius="md"
                            p={3}
                          >
                            <Text fontSize="md" fontWeight="bold" mb={2}>
                              Latest Update
                            </Text>
                          </Box>
                          <Box
                            position="absolute"
                            top="58%"
                            left="48%"
                            transform="translateX(-50%)"
                            width="80%"
                            maxWidth="220px"
                            textAlign="left"
                            borderRadius="md"
                            overflow={"hidden"}
                            height={"140px"}
                            p={3}
                          >
                            <Text fontSize="xs" fontWeight="medium">
                              {backOfPassMessage}
                            </Text>
                          </Box>
                        </>
                      )}
                    </Box>
                  )}
                </>
              ) : (
                <Text
                  color="gray.500"
                  fontSize="sm"
                  w="100%"
                  textAlign="center"
                >
                  Preview images cannot be displayed due to limited screen width
                </Text>
              )}
            </HStack>
          </TabPanel>

          {/* Google Tab */}
          <TabPanel>
            <HStack
              justifyContent="center"
              w="100%"
              overflow="hidden"
              transform={imagesVisible ? `scale(${PREVIEW_SCALE})` : undefined}
              transformOrigin="top center"
              mb={imagesVisible ? PREVIEW_HEIGHT_OFFSET : undefined}
            >
              {imagesVisible ? (
                <>
                  <Box position="relative" w={`${LOCK_SCREEN_WIDTH}px`}>
                    <Image
                      src={googleWalletPassFront}
                      alt="Google Wallet Pass Front"
                      width={LOCK_SCREEN_WIDTH}
                      height={LOCK_SCREEN_HEIGHT}
                      objectFit="contain"
                    />
                  </Box>
                  <Box position="relative" w={`${WALLET_BACK_WIDTH}px`}>
                    <Image
                      src={googleWalletPassBack}
                      alt="Google Wallet Pass Back"
                      width={WALLET_BACK_WIDTH}
                      height={WALLET_BACK_HEIGHT}
                      objectFit="contain"
                    />
                    {showBackOfPassContent && (
                      <Box
                        position="absolute"
                        top="54.5%"
                        left="46.5%"
                        transform="translateX(-50%)"
                        width="80%"
                        maxWidth="250px"
                        textAlign="left"
                        borderRadius="md"
                        overflow={"hidden"}
                        height={"100px"}
                        p={3}
                      >
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          color={"#ddcdbc"}
                        >
                          {backOfPassMessage}
                        </Text>
                      </Box>
                    )}
                  </Box>
                </>
              ) : (
                <Text
                  color="gray.500"
                  fontSize="sm"
                  w="100%"
                  textAlign="center"
                >
                  Preview images cannot be displayed due to limited screen width
                </Text>
              )}
            </HStack>
          </TabPanel>
        </TabPanels>
      </Tabs>
    </VStack>
  );
};
